/**
 * Updates the recently viewed products in localStorage.
 * Storage access can be blocked inside sandboxed frames or privacy-restricted browsers,
 * so every operation must fail safely.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Returns localStorage when it is available.
   * @returns {Storage | null}
   */
  static #getStorage() {
    try {
      return window.localStorage;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    if (!productId) return;

    const viewedProducts = this.getProducts().filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts.length = Math.min(viewedProducts.length, this.#MAX_PRODUCTS);

    try {
      this.#getStorage()?.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch (_error) {
      // Storage can be unavailable in sandboxed or privacy-restricted contexts.
    }
  }

  static clearProducts() {
    try {
      this.#getStorage()?.removeItem(this.#STORAGE_KEY);
    } catch (_error) {
      // Storage can be unavailable in sandboxed or privacy-restricted contexts.
    }
  }

  /**
   * Retrieves the list of recently viewed products.
   * @returns {string[]} The list of viewed product IDs.
   */
  static getProducts() {
    try {
      const rawValue = this.#getStorage()?.getItem(this.#STORAGE_KEY);
      const parsedValue = JSON.parse(rawValue || '[]');

      return Array.isArray(parsedValue)
        ? parsedValue.filter((productId) => typeof productId === 'string')
        : [];
    } catch (_error) {
      return [];
    }
  }
}
